// Shopify Standard Product Taxonomy — Jewelry lives under aa-6 (NOT aa-1, which is Clothing)
// Reference: https://github.com/Shopify/product-taxonomy/blob/main/data/categories/aa_apparel_accessories.yml

export interface ShopifyCategory {
  label: string;
  gid: string;
  shortId: string;
  description?: string;
}

const gid = (shortId: string) => `gid://shopify/TaxonomyCategory/${shortId}`;

/** Map legacy wrong aa-1-* IDs (clothing) saved before the fix → correct aa-6-* jewelry IDs */
export const LEGACY_CATEGORY_ID_FIXES: Record<string, string> = {
  // Old "rings" pointed at Outfit Sets (aa-1-11)
  'aa-1-11': 'aa-6-9',
  'aa-1-11-1': 'aa-6-9',
  'aa-1-11-3': 'aa-6-9',
  'aa-1-11-4': 'aa-6-9',
  // Old "necklaces" pointed at One-Pieces (aa-1-9)
  'aa-1-9': 'aa-6-8',
  'aa-1-9-1': 'aa-6-8',
  'aa-1-9-3': 'aa-6-8',
  // Old "bracelets" pointed at Baby & Toddler Clothing (aa-1-2)
  'aa-1-2': 'aa-6-3',
  'aa-1-2-1': 'aa-6-3',
  'aa-1-2-2': 'aa-6-3',
  'aa-1-2-4': 'aa-6-3',
  // Old "earrings" — aa-1-4 is Dresses
  'aa-1-4': 'aa-6-6',
  'aa-1-4-1': 'aa-6-6',
  'aa-1-4-2': 'aa-6-6',
  'aa-1-4-4': 'aa-6-6',
  // Old "watches" pointed at Clothing Tops (aa-1-13) ← user-reported bug
  'aa-1-13': 'aa-6-11',
  // Old misc aa-1-* jewelry mappings
  'aa-1-1': 'aa-6-2',   // was body jewelry → Clothing Accessories
  'aa-1-3': 'aa-6-4',   // was brooches → Activewear
  'aa-1-8': 'aa-6-7',   // was jewelry sets → Outerwear
  'aa-1-12': 'aa-6-5',  // was charms → Pants
};

/** Normalize a stored category GID, fixing legacy wrong IDs from before taxonomy correction */
export function normalizeCategoryGid(categoryGid: string | undefined | null): string | undefined {
  if (!categoryGid?.trim()) return undefined;
  const trimmed = categoryGid.trim();
  const shortId = trimmed.replace(/^gid:\/\/shopify\/TaxonomyCategory\//, '');
  const fixedShortId = LEGACY_CATEGORY_ID_FIXES[shortId] || shortId;
  return gid(fixedShortId);
}

// Jewelry categories — all under Apparel & Accessories > Jewelry (aa-6)
export const JEWELRY_CATEGORIES: Record<string, ShopifyCategory> = {
  'rings': {
    label: 'Rings',
    gid: gid('aa-6-9'),
    shortId: 'aa-6-9',
    description: 'Apparel & Accessories > Jewelry > Rings',
  },
  'rings-engagement': {
    label: 'Rings (Engagement)',
    gid: gid('aa-6-9'),
    shortId: 'aa-6-9',
    description: 'Engagement rings — Shopify taxonomy uses parent Rings category',
  },
  'rings-wedding': {
    label: 'Rings (Wedding & Anniversary Bands)',
    gid: gid('aa-6-9'),
    shortId: 'aa-6-9',
    description: 'Wedding bands — Shopify taxonomy uses parent Rings category',
  },
  'rings-fashion': {
    label: 'Rings (Fashion)',
    gid: gid('aa-6-9'),
    shortId: 'aa-6-9',
    description: 'Fashion rings — Shopify taxonomy uses parent Rings category',
  },

  'necklaces': {
    label: 'Necklaces',
    gid: gid('aa-6-8'),
    shortId: 'aa-6-8',
    description: 'Apparel & Accessories > Jewelry > Necklaces',
  },
  'necklaces-chain': {
    label: 'Necklaces (Chain)',
    gid: gid('aa-6-8'),
    shortId: 'aa-6-8',
    description: 'Chain necklaces',
  },
  'necklaces-pendant': {
    label: 'Necklaces (Pendant)',
    gid: gid('aa-6-8'),
    shortId: 'aa-6-8',
    description: 'Pendant necklaces',
  },

  'bracelets': {
    label: 'Bracelets',
    gid: gid('aa-6-3'),
    shortId: 'aa-6-3',
    description: 'Apparel & Accessories > Jewelry > Bracelets',
  },
  'bracelets-chain': {
    label: 'Bracelets (Chain)',
    gid: gid('aa-6-3'),
    shortId: 'aa-6-3',
    description: 'Chain bracelets',
  },
  'bracelets-bangle': {
    label: 'Bracelets (Bangle)',
    gid: gid('aa-6-3'),
    shortId: 'aa-6-3',
    description: 'Bangle bracelets',
  },
  'bracelets-tennis': {
    label: 'Bracelets (Tennis)',
    gid: gid('aa-6-3'),
    shortId: 'aa-6-3',
    description: 'Tennis bracelets',
  },

  'earrings': {
    label: 'Earrings',
    gid: gid('aa-6-6'),
    shortId: 'aa-6-6',
    description: 'Apparel & Accessories > Jewelry > Earrings',
  },
  'earrings-stud': {
    label: 'Earrings (Stud)',
    gid: gid('aa-6-6'),
    shortId: 'aa-6-6',
    description: 'Stud earrings',
  },
  'earrings-hoop': {
    label: 'Earrings (Hoop)',
    gid: gid('aa-6-6'),
    shortId: 'aa-6-6',
    description: 'Hoop earrings',
  },
  'earrings-drop': {
    label: 'Earrings (Drop & Dangle)',
    gid: gid('aa-6-6'),
    shortId: 'aa-6-6',
    description: 'Drop and dangle earrings',
  },

  'watches': {
    label: 'Watches',
    gid: gid('aa-6-11'),
    shortId: 'aa-6-11',
    description: 'Apparel & Accessories > Jewelry > Watches',
  },

  'body-jewelry': {
    label: 'Body Jewelry',
    gid: gid('aa-6-2'),
    shortId: 'aa-6-2',
    description: 'Apparel & Accessories > Jewelry > Body Jewelry',
  },

  'brooches': {
    label: 'Brooches & Lapel Pins',
    gid: gid('aa-6-4'),
    shortId: 'aa-6-4',
    description: 'Apparel & Accessories > Jewelry > Brooches & Lapel Pins',
  },

  'charms': {
    label: 'Charms & Pendants',
    gid: gid('aa-6-5'),
    shortId: 'aa-6-5',
    description: 'Apparel & Accessories > Jewelry > Charms & Pendants',
  },

  'jewelry-sets': {
    label: 'Jewelry Sets',
    gid: gid('aa-6-7'),
    shortId: 'aa-6-7',
    description: 'Apparel & Accessories > Jewelry > Jewelry Sets',
  },
};

/** Internal inventory-database TYPE codes → Shopify product type labels */
export const INVENTORY_TYPE_TO_PRODUCT_TYPE: Record<string, string> = {
  RING: 'Ring',
  NEC: 'Necklace',
  BRAC: 'Bracelet',
  EAR: 'Earrings',
  PEND: 'Pendant',
  MISC: 'Jewelry',
  PEARL: 'Necklace',
  DSTUD: 'Earrings',
  WAT: 'Watches',
  CHAIN: 'Chain',
  CHARM: 'Charm',
  PIN: 'Brooch',
  PEN: 'Jewelry',
  SF: 'Jewelry',
  COIN: 'Jewelry',
  SET: 'Jewelry Set',
  PANDORA: 'Charm',
  // Legacy short codes
  JEW: 'Jewelry',
  RNG: 'Ring',
  BRC: 'Bracelet',
  NCK: 'Necklace',
  BRO: 'Brooch',
  CHN: 'Chain',
  CHM: 'Charm',
};

/** Use entry-form product type for Shopify; expand internal inventory codes if present */
export function normalizeProductTypeForShopify(productType: string | undefined | null): string {
  if (!productType?.trim()) return '';
  const trimmed = productType.trim();
  const mapped = INVENTORY_TYPE_TO_PRODUCT_TYPE[trimmed.toUpperCase()];
  return mapped || trimmed;
}

export function suggestCategoryByProductType(productType: string): ShopifyCategory | null {
  const type = normalizeProductTypeForShopify(productType).toLowerCase();
  if (!type) return null;

  if (type.includes('engagement')) return JEWELRY_CATEGORIES['rings-engagement'];
  if (type.includes('wedding') || (type.includes('band') && !type.includes('watch'))) {
    return JEWELRY_CATEGORIES['rings-wedding'];
  }
  if (type.includes('ring')) return JEWELRY_CATEGORIES['rings'];

  if (type.includes('pendant') && type.includes('necklace')) return JEWELRY_CATEGORIES['necklaces-pendant'];
  if (type.includes('chain') && type.includes('necklace')) return JEWELRY_CATEGORIES['necklaces-chain'];
  if (type.includes('necklace')) return JEWELRY_CATEGORIES['necklaces'];
  if (type.includes('pendant')) return JEWELRY_CATEGORIES['charms'];

  if (type.includes('tennis') && type.includes('bracelet')) return JEWELRY_CATEGORIES['bracelets-tennis'];
  if (type.includes('bangle')) return JEWELRY_CATEGORIES['bracelets-bangle'];
  if (type.includes('bracelet')) return JEWELRY_CATEGORIES['bracelets'];

  if (type.includes('stud')) return JEWELRY_CATEGORIES['earrings-stud'];
  if (type.includes('hoop')) return JEWELRY_CATEGORIES['earrings-hoop'];
  if (type.includes('drop') || type.includes('dangle')) return JEWELRY_CATEGORIES['earrings-drop'];
  if (type.includes('earring')) return JEWELRY_CATEGORIES['earrings'];

  if (type.includes('watch')) return JEWELRY_CATEGORIES['watches'];
  if (type.includes('brooch') || type.includes('pin')) return JEWELRY_CATEGORIES['brooches'];
  if (type.includes('charm')) return JEWELRY_CATEGORIES['charms'];
  if (type.includes('anklet')) return JEWELRY_CATEGORIES['body-jewelry'];

  return null;
}

export function getAllCategories(): ShopifyCategory[] {
  // Dedupe by gid so dropdown doesn't show duplicate GIDs for ring sub-types
  const seen = new Set<string>();
  return Object.values(JEWELRY_CATEGORIES).filter(c => {
    if (seen.has(c.gid)) return false;
    seen.add(c.gid);
    return true;
  });
}
